<script context="module" lang="ts">
  const MAX_ROTATION = 60 /* deg */

  export enum DIRECTION {
    UP = 1,
    DOWN,
    LEFT,
    RIGHT,
  }
  export enum AXIS {
    PITCH = 1,
    YAW,
  }
</script>

<script lang="ts">
  import { deg2rad } from "@/libs/geometry/geometry"
  import { clamp, round } from "@/libs/math"
  import { fade } from "@/libs/ui/transition"
  import { onMount } from "svelte"
  import { tweened } from "svelte/motion"
  import DirectionIndicator from "./DirectionIndicator.svelte"

  const INDICATOR_ANGLE_MAP = {
    [DIRECTION.UP]: 90,
    [DIRECTION.DOWN]: -90,
    [DIRECTION.LEFT]: 180,
    [DIRECTION.RIGHT]: 0,
  }

  export let axis: AXIS
  export let rotation: number
  export let direction: DIRECTION | null = null
  export let showTarget = false

  let container: HTMLDivElement
  let elHeight = 0
  let elWidth = 0

  onMount(() => {
    const observer = new ResizeObserver(([entry]) => {
      elHeight = entry.contentRect.height
      elWidth = entry.contentRect.width
    })

    observer.observe(container)

    return () => observer.disconnect()
  })

  const angle = tweened(rotation, { duration: 100 })
  const guideGradientOpacity = tweened(0, { duration: 100 })

  $: angle.set(
    round((90 / MAX_ROTATION) * clamp(rotation, { min: -MAX_ROTATION, max: MAX_ROTATION }), 2),
  )
  $: guideGradientOpacity.set(direction ? 0 : 1)

  const baseHeight = 389
  const baseWidth = 336

  $: isYaw = axis === AXIS.YAW
  $: arc = round(
    ((isYaw ? baseWidth : baseHeight) / 2) * Math.sign($angle) * Math.sin(deg2rad($angle)),
    2,
  )
  $: sweep = Math.sign($angle) < 0 ? 1 : 0

  $: noDirectionErase = direction === null ? () => undefined : <T,>(value: T) => value
  $: angleX = noDirectionErase(isYaw ? 0 : $angle)
  $: angleY = noDirectionErase(isYaw ? $angle : 0)
  $: angleZ = noDirectionErase(direction && INDICATOR_ANGLE_MAP[direction])
  $: guideGradientDirection = isYaw ? "to bottom" : "to right"

  const viewBox = `0 0 ${baseWidth} ${baseHeight}`
</script>

<div bind:this={container} class={`relative aspect-[336/389] h-full w-auto ${$$props.class}`}>
  {#if showTarget}
    <svg
      {viewBox}
      xmlns="http://www.w3.org/2000/svg"
      class="absolute inset-0 h-full w-full opacity-30"
    >
      <path
        d={isYaw ? "M168 1v387" : "M1 194L335 194"}
        stroke="currentColor"
        stroke-dasharray="8 8"
        stroke-width="1.5"
        fill="none"
      />
    </svg>
  {/if}

  <svg
    {viewBox}
    xmlns="http://www.w3.org/2000/svg"
    class="guide absolute inset-0 h-full w-full drop-shadow-[0px_0px_6px_currentColor]"
    class:guide-pitch={!isYaw}
    class:guide-yaw={isYaw}
    style:mask={$guideGradientOpacity === 1
      ? "none"
      : `linear-gradient(
        ${guideGradientDirection},
        rgba(0, 0, 0, 1),
        rgba(0, 0, 0, ${$guideGradientOpacity}) 41.6%,
        rgba(0, 0, 0, ${$guideGradientOpacity}) 58.4%,
        rgba(0, 0, 0, 1)
      )`}
  >
    <path
      d={isYaw
        ? `M168,1 A${arc},193 0 0,${sweep} 168,388`
        : `M1,194 A167,${arc} 0 0,${sweep} 335,194`}
      stroke-linecap="round"
      stroke-width="1.75"
      stroke="currentColor"
      fill="none"
    />
  </svg>

  {#if direction}
    {#key direction}
      <div
        class="direction-indicator-container absolute left-1/2 top-1/2 h-auto w-[14.67%]"
        style:--radius={(isYaw ? elWidth : elHeight) / 2}
        style:--angle-x={angleX}
        style:--angle-y={angleY}
        style:--angle-z={angleZ}
        in:fade|local={{ delay: 500, duration: 500 }}
        out:fade|local={{ duration: direction === null ? 0 : 500 }}
      >
        <DirectionIndicator class="drop-shadow-[0px_0px_3px_currentColor]" />
      </div>
    {/key}
  {/if}
</div>

<style lang="postcss">
  .direction-indicator-container {
    transform-origin: center center calc(-1px * var(--radius));
    transform: translateX(-50%) translateY(-50%) rotateY(calc(-1deg * var(--angle-y, 0)))
      rotateX(calc(-1deg * var(--angle-x, 0))) rotateZ(calc(-1deg * var(--angle-z, 0)));
  }

  .guide path {
    animation-duration: 0.75s;
    animation-timing-function: ease-out;
    animation-fill-mode: forwards;
    animation-iteration-count: 1;
  }

  .guide-yaw path {
    animation-name: fill-yaw;

    stroke-dasharray: 195 390;
    stroke-dashoffset: 195;

    @keyframes fill-yaw {
      0% {
        stroke-dasharray: 195 390;
        stroke-dashoffset: 195;
      }
      100% {
        stroke-dasharray: 195 0;
        stroke-dashoffset: 0;
      }
    }
  }

  .guide-pitch path {
    animation-name: fill-pitch;

    stroke-dasharray: 168 336;
    stroke-dashoffset: 168;

    @keyframes fill-pitch {
      0% {
        stroke-dasharray: 168 336;
        stroke-dashoffset: 168;
      }
      100% {
        stroke-dasharray: 168 0;
        stroke-dashoffset: 0;
      }
    }
  }
</style>
